import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import AppraiserModal from '../../../appraisals/appraisers/modals/AppraiserModal';
import { LoadPanel } from 'devextreme-react/load-panel';
import { ModalSummaryHeader } from '../../common/ApplicationComponents';
import { ApplicationStatusTypeOptions } from '@apply/constants';
import { NoticeTypeOptions, NoticeStatusType } from '@apply/constants';
import { formatDateTime } from '@apply/ui';
import { handleAdminSuccess, handleAdminError } from '../../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';

const RestoreApplicationModal = () => {
    const { isOpen, id } = useSelector((state) => state.application.restoreApplicationModal);
    const { dispatch } = store;

    const [application, set_application] = useState(null);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        form: { paddingTop: '10px' },
        formRow: { marginBottom: '0' },
        formColumn: { padding: '5px' },
        hrDefault: { marginTop: '0' },
        nonEditable: {
            backgroundColor: '#f7f7f7',
            padding: '5px 10px',
            margin: '5px',
        },
        noPaddingRight: { paddingLeft: '0' },
        noPaddingLeft: { paddingLeft: '0' },
        padding10Bottom: { marginBottom: '10px' },
        leftLabel: { paddingLeft: '0', paddingTop: '8px', paddingRight: '0' },
    };

    useEffect(() => {
        async function isOpenChanged() {
            resetState();

            if (!isOpen || !id) {
                return;
            }

            await loadApplication(id);
        }

        isOpenChanged();
    }, [isOpen, id]);

    const resetState = () => {
        set_application(null);
        set_loadingOpen(false);
    };

    const loadApplication = async (id) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            const response = await applicationApi.getRestoreLoan(id);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            const appl = response.result;
            set_application(appl);

            set_loadingOpen(false);
        } catch (ex) {
            set_loadingOpen(false);
        }
    };

    const _handleClose = () =>
        dispatch(
            applicationActions.setRestoreApplicationModal({
                restoreApplicationModal: { isOpen: false },
            })
        );

    const submitChanges = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const response = await applicationApi.setRestoreLoan(application.id);
            if (response.statusCode !== 200) {
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Changes saved successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const getNoticeStyle = () => {
        switch (application.noticeStatusId) {
            case NoticeStatusType.Emailed:
            case NoticeStatusType.Generated:
                return { paddingTop: '8px', color: 'green' };
            case NoticeStatusType.Error:
                return { paddingTop: '8px', color: 'darkred' };
            default:
                return { paddingTop: '8px', color: 'gray' };
        }
    };

    const getNoticeTitle = () => {
        var noticeTypeName = NoticeTypeOptions.find((f) => f.id === application.noticeTypeId)?.name;
        switch (application.noticeStatusId) {
            case NoticeStatusType.Emailed:
                return `The notice '${noticeTypeName}' was sent at ${formatDateTime(application.NoticeDate)}`;
            case NoticeStatusType.Generated:
                return `The notice '${noticeTypeName}' has been generated but not yet emailed`;
            case NoticeStatusType.Pending:
                return `The notice '${noticeTypeName}' to be sent at ${formatDateTime(application.NoticeDate)} will be cancelled`;
            case NoticeStatusType.Error:
                return `The notice '${noticeTypeName}' experiences an Error`;
            default:
                return 'No Notice has been sent or scheduled';
        }
    };

    if (!isOpen || !application) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '680px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='declineAppraisalForm'
                                    onSubmit={(event) => submitChanges(event)}
                                >
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1 style={{ whiteSpace: 'nowrap' }}>Restore Application</h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={_handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>

                                    <div
                                        className='row'
                                        style={styles.formRow}
                                    >
                                        <div
                                            className='columns small-12 large-12'
                                            style={getNoticeStyle()}
                                        >
                                            <i title={getNoticeTitle()}>
                                                <FontAwesomeIcon icon={faPencil} />
                                            </i>
                                            &nbsp;&nbsp;{getNoticeTitle()}
                                        </div>
                                    </div>

                                    <ModalSummaryHeader application={application} />
                                    <div
                                        className='scrollable-content'
                                        style={{ margin: '10px' }}
                                    >
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            <h2 className='decorated'>
                                                {application.properties.length === 1 ? 'Property' : 'Properties'}
                                            </h2>
                                            <ul>
                                                {application.properties.map((p) => {
                                                    return (
                                                        <li key={`p${p.id}`}>
                                                            {p.description && (
                                                                <div>
                                                                    <b>Property {p.bfiNumberSuffix}</b>
                                                                    {p.address && (
                                                                        <span>
                                                                            : {p.address?.street1} {p.address?.street2}{' '}
                                                                            {p.address?.city}, {p.address?.state}{' '}
                                                                            {p.address?.zipCode}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-12'
                                                    style={styles.formColumn}
                                                >
                                                    Clicking 'Submit' will restore this application to the following State:{' '}
                                                    <b>
                                                        {
                                                            ApplicationStatusTypeOptions.find(
                                                                (s) => s.id === application.lastStatusId
                                                            ).name
                                                        }
                                                    </b>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <AppraiserModal />
        </>
    );
};

export default RestoreApplicationModal;
